'use client';
import React, { FC, useEffect } from 'react';
import { Button } from '@/components';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { useRouter as useLocaleRouter, usePathname } from '@/navigation';

import {
  deleteAdminPreviewCookieFromClient,
  PREVIEW_USER_UUID_KEY,
} from '@/utils/adminPreviewModeHelpers';

export type AdminPreviewNotificationProps = {
  adminName: string | undefined;
  userEmail: string | undefined;
};

const AdminPreviewNotification: FC<AdminPreviewNotificationProps> = ({
  adminName,
  userEmail,
}) => {
  const pathname = usePathname();
  const { refresh } = useRouter();
  const localeRouter = useLocaleRouter();

  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams?.get(PREVIEW_USER_UUID_KEY)) {
      // remove preview user query param after it is consumed and persisted in a cookie
      if (pathname) localeRouter.replace(pathname);
    }
  }, [pathname, localeRouter, searchParams]);

  if (!adminName) {
    return null;
  }

  function onPreviewExit() {
    deleteAdminPreviewCookieFromClient();
    // refresh the page so server side props can refetch page data
    refresh();
  }

  return (
    <div
      data-testid="admin-preview-notification"
      className="flex flex-wrap items-center justify-between rounded-md bg-indigo-pastel p-5"
    >
      {userEmail && (
        <p>
          Hey {adminName}, you're previewing{' '}
          <span className="font-bold">{userEmail}</span>'s account
        </p>
      )}
      <Button
        style={BUTTON_STYLES.link}
        ctaText="Close Preview"
        onClick={onPreviewExit}
      />
    </div>
  );
};

export default React.memo(AdminPreviewNotification);
